<script>
import Vue from "vue";
import store from "@/state/store";

import { required, minLength, maxLength, sameAs } from "vuelidate/lib/validators";

import { mapState } from "vuex";
import appConfig from "@/app.config";
import Layout from "@/router/layouts/auth";
import {
  AccountStatus,
  notificationMethods, SessionLoginState
} from "@/state/helpers";

import VueCryptojs    from 'vue-cryptojs';
Vue.use(VueCryptojs);

/**
 * Forgot Password component
 */
export default {
  page: {
    title: "Confirm login",
    meta: [{ name: "description", content: appConfig.description }]
  },
  components: { Layout },
  data() {
    return {
      password: "",
      wrong_password_count: 0,
      submitted: false
    };
  },
  computed: {
    notification() {
      return this.$store ? this.$store.state.notification : null;
    }
  },
  validations: {
    password: { required },
  },
  methods: {
    ...notificationMethods,
    async password_recovery() {
      const router = this.$router; // Promise hell...
      this.$swal({
        title: this.$t('recovery.title'),
        text: this.$t('recovery.text'),
        confirmButtonText: this.$t('generic.yes'),
        cancelButtonText: this.$t('generic.no'),
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: true,
      }).then((result) => {
        if(result.value) {
          fetch(process.env.VUE_APP_ROOT_API + 'v1/@me/acsrf-token', {credentials: 'include'})
            .then(response => {
              if(response.ok){
                return response.json();
              }
            })
            .then(data => {
              /* Actual request */
              let payload = {
                acsrf_token: data.acsrf_token
              };
              let cftools_id = store.getters.getUser();
              fetch(process.env.VUE_APP_ROOT_API + `v1/@me/${cftools_id}/account-recovery`, {
                method: 'POST',
                body: JSON.stringify(payload),
                credentials: 'include'
              })
                  .then(response => {
                    return response.json();
                  })
                  .then(data => {
                    if(!data.status) {
                      if(data.error === 'invalid-state') {
                        /* Local state got desynchronized, reevaluating */
                        console.log('Resolving state desynchronization');
                        this.$router.push({name: "login"}).catch((error) => {});
                      } else if(data.error === 'rate-limited') this.handle_rate_limit_error();
                      else this.handle_fail_and_reset();
                    }
                    else this.$router.push({name: "recovery"}).catch((error) => {});
                  })
                  .catch(error => {
                    console.log(`[ERROR] ${error}`);
                    this.handle_fail_and_reset();
                  });
              /* --- */
            })
            .catch(error => {
              console.log(error);
              this.handle_fail_and_reset();
            });
        }
      });
    },
    handle_fail_and_reset() {
      this.submitted = false;
      this.$swal( {
        icon: 'error',
        title: this.$t('error.server.generic.title'),
        text: this.$t('error.server.generic.message')
      });
    },
    handle_bad_password() {
      this.submitted = false;
      this.wrong_password_count++;
      this.password = "";
      this.$swal( {
        icon: 'error',
        title: this.$t('error.server.bad_password.title'),
        text: this.$t('error.server.bad_password.message')
      });
    },
    handle_rate_limit_error() {
      this.submitted = false;
      this.$swal( {
        icon: 'warning',
        title: this.$t('error.server.ratelimit.title'),
        text: this.$t('error.server.ratelimit.message')
      });
    },
    handle_success() {
      this.$router.push({name: "index"}).catch((error) => {});
    },
    try_submit() {
      if(this.submitted) return;
      this.submitted = true;

      this.$v.$touch();
      if(this.$v.$invalid) {
        this.submitted = false;
      } else {
        // Plain text password never leaves the client
        const hashed_password = this.CryptoJS.SHA256(this.password).toString(this.CryptoJS.enc.Hex);
        fetch(process.env.VUE_APP_ROOT_API + 'v1/@me/acsrf-token', {credentials: 'include'})
        .then(response => {
          if(response.ok){
            return response.json();
          }
        })
        .then(data => {
          /* Actual request */
          let payload = {
            acsrf_token: data.acsrf_token,
            password: hashed_password
          };
          fetch(process.env.VUE_APP_ROOT_API + 'v1/@me/confirm-password', {
            method: 'POST',
            body: JSON.stringify(payload),
            credentials: 'include'
          })
              .then(response => {
                return response.json();
              })
              .then(data => {
                if(!data.status) {
                  if(data.error == 'invalid-state') {
                    /* Local state got desynchronized, reevaluating */
                    console.log('Resolving state desynchronization');
                    this.$router.push({name: "login"}).catch((error) => {});
                  } else if(data.error == 'rate-limited') this.handle_rate_limit_error();
                    else if(data.error == 'bad-password') this.handle_bad_password();
                    else this.handle_fail_and_reset();
                }
                else {
                  store.commit('login', data.state);
                  this.handle_success();
                }
              })
              .catch(error => {
                console.log(`[ERROR] ${error}`);
                this.handle_fail_and_reset();
              });
          /* --- */
        })
        .catch(error => {
          console.log(error);
          this.handle_fail_and_reset();
        });
      }
    }
  }
};
</script>

<template>
  <Layout>
    <div class="row justify-content-center">
      <div class="col-md-8 col-lg-6 col-xl-5">
        <div class="card overflow-hidden">
          <div class="bg-soft-primary">
            <div class="row">
              <div class="col-12">
                <div class="text-primary p-3">
                  <h4 class="text-white text-right p-0">{{$t('login.password.title')}}</h4>
                </div>
              </div>
            </div>
          </div>
          <div class="card-body pt-0">
            <div>
              <router-link tag="a" to="/">
                <div class="avatar-md profile-user-wid mb-4">
                  <span class="avatar-title rounded-circle bg-light">
                    <img src="@/assets/images/logo.svg" alt height="34" />
                  </span>
                </div>
              </router-link>
            </div>
            <div
                v-if="notification.message"
                :class="'alert ' + notification.type"
            >{{notification.message}}</div>
            <div class="p-2">
              <form @submit.prevent="try_submit">
                <div class="form-group">
                  <label for="password">{{$t('login.password.prompt')}}</label>
                  <input
                    type="password"
                    v-model.trim="password"
                    class="form-control"
                    id="password"
                    :class="{ 'is-invalid': $v.password.$error }"
                    @change="$v.password.$touch()"
                  />
                  <div v-if="$v.password.$error" class="invalid-feedback">
                    <span v-if="!$v.password.required">{{$t('setup.password.errors.required')}}</span>
                  </div>
                </div>
                <div class="mt-3">
                  <b-button type="submit" variant="primary" class="btn-block">{{$t('login.button_text')}}</b-button>
                </div>
              </form>
            </div>
            <div class="p-2">
              <div class="small text-center">
                <button class="btn btn-sm btn-xs btn-info" v-on:click="password_recovery">
                  {{$t('login.password_recovery')}}
                </button>
              </div>
            </div>
          </div>
          <!-- end card-body -->
        </div>
        <!-- end card -->
      </div>
      <!-- end col -->
    </div>
    <!-- end row -->
    <div class="mt-3 text-center">
      <small>
        © 2017 - {{new Date().getFullYear()}} {{$t('generic.legal_disclaimer')}} {{$t('generic.provider')}}<br>
        <small style="font-size: 10px;">
          <a href="https://cftools.cloud/legal/imprint" style="color: unset !important;">Imprint</a>
          /
          <a href="https://cftools.cloud/legal/privacy" style="color: unset !important;">Privacy policy</a>
        </small>
      </small>
    </div>
  </Layout>
</template>

<style lang="scss" module></style>
